// screens/RDProjectDetailScreen.js - Screen 12
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { SyncBar, PhotoThumb, CommentThread, EmptyState } from '../components/shared';
import PhotoViewer from '../components/PhotoViewer';
import api, { getErrorMessage } from '../utils/api';

const STAGES = ['Concept', 'Prototype', 'Sample', 'Pre-production', 'Final'];

export default function RDProjectDetailScreen() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const toast = useToast();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeStage, setActiveStage] = useState('Concept');
  const [viewerPhoto, setViewerPhoto] = useState(null);
  const [showAddPhoto, setShowAddPhoto] = useState(false);
  const [stepLabel, setStepLabel] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef();

  const canAdd = ['R&D', 'R&D Photographer', 'Admin'].includes(user?.role);

  useEffect(() => { loadProject(); }, [id]);

  const loadProject = async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get(`/api/rd/projects/${id}`);
      setData(d);
    } catch { toast.error('Failed to load project'); }
    finally { setLoading(false); }
  };

  const stagePhotos = data?.photos.filter(p => p.stage === activeStage) || [];

  const handleFileCapture = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !stepLabel.trim()) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('project_id', id);
      formData.append('stage', activeStage);
      formData.append('step_label', stepLabel.trim());
      formData.append('department', 'rd');
      formData.append('device_id', navigator.userAgent.slice(0, 40));
      formData.append('photo', file);

      await api.post('/api/rd/photos', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success('Photo added');
      setStepLabel('');
      setShowAddPhoto(false);
      loadProject();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleAddPhoto = () => {
    if (!stepLabel.trim()) { toast.error('Step label required'); return; }
    fileInputRef.current?.click();
  };

  const handleNewComment = (comment) => {
    setData(d => ({ ...d, comments: [...(d.comments || []), comment] }));
  };

  if (loading) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div className="spinner spinner-lg" />
    </div>
  );
  if (!data) return null;

  const { project, photos, comments } = data;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', minHeight: '100dvh' }}>
      <SyncBar />
      <div className="page-header">
        <button className="btn btn-ghost btn-icon" onClick={() => navigate('/rd')}>←</button>
        <div style={{ textAlign: 'center', flex: 1, minWidth: 0 }}>
          <div style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, fontSize: '0.9375rem', color: 'var(--accent-secondary)' }}>
            {project.reference}
          </div>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {project.name}
          </div>
        </div>
        <button className="btn btn-secondary btn-sm" onClick={() => alert('In production: export R&D PDF/ZIP labelled with project reference')}>
          ↓ Export
        </button>
      </div>

      <div className="page-content">
        {/* Stage tabs */}
        <div className="stage-tabs" style={{ marginBottom: 16 }}>
          {STAGES.map(stage => {
            const count = photos.filter(p => p.stage === stage).length;
            return (
              <button key={stage} className={`stage-tab ${activeStage === stage ? 'active' : ''}`}
                onClick={() => setActiveStage(stage)}>
                {stage}
                {count > 0 && <span style={{ marginLeft: 5, opacity: 0.7 }}>({count})</span>}
              </button>
            );
          })}
        </div>

        <div className="desktop-two-col">
          {/* Left: Photos */}
          <div>
            {/* Add photo button */}
            {canAdd && (
              <div style={{ marginBottom: 16 }}>
                {showAddPhoto ? (
                  <div className="card" style={{ padding: 16 }}>
                    <div className="form-group" style={{ marginBottom: 12 }}>
                      <label className="form-label">Step Label (required, max 40 chars)</label>
                      <input type="text" value={stepLabel} onChange={e => setStepLabel(e.target.value.slice(0, 40))}
                        className="input-field" placeholder="e.g. Front angle, Detail close-up..."
                        autoFocus
                        onKeyDown={e => { if (e.key === 'Enter') handleAddPhoto(); }} />
                      <div className="form-hint">{stepLabel.length}/40 characters</div>
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button className="btn btn-secondary" onClick={() => { setShowAddPhoto(false); setStepLabel(''); }}>Cancel</button>
                      <button className="btn btn-primary" onClick={handleAddPhoto} disabled={!stepLabel.trim() || uploading}>
                        {uploading ? 'Uploading...' : '📷 Take Photo'}
                      </button>
                    </div>
                  </div>
                ) : (
                  <button className="btn btn-primary btn-full" onClick={() => setShowAddPhoto(true)}>
                    + Add Photo to {activeStage}
                  </button>
                )}
              </div>
            )}

            {/* Photo grid */}
            {stagePhotos.length === 0 ? (
              <EmptyState icon="🔬" title={`No ${activeStage} photos`}
                message="Add photos to document this stage" />
            ) : (
              <div className="photo-grid">
                {stagePhotos.map(p => (
                  <PhotoThumb key={p.id} photo={p} onClick={() => setViewerPhoto(p)} />
                ))}
              </div>
            )}
          </div>

          {/* Right: Comments + project info */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* Project info */}
            <div className="card">
              <div className="section-title" style={{ marginBottom: 10 }}>Project Info</div>
              <div style={{ fontSize: '0.875rem', display: 'flex', flexDirection: 'column', gap: 6 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: 'var(--text-muted)' }}>Status</span>
                  <span className={`badge ${project.status === 'open' ? 'badge-success' : 'badge-secondary'}`}>{project.status}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: 'var(--text-muted)' }}>Total photos</span>
                  <span style={{ fontWeight: 600 }}>{photos.length}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: 'var(--text-muted)' }}>Site</span>
                  <span>{project.site_name}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: 'var(--text-muted)' }}>Created</span>
                  <span>{new Date(project.created_at).toLocaleDateString()}</span>
                </div>
              </div>

              {/* Stage summary */}
              <div style={{ marginTop: 14 }}>
                <div className="section-title">Stages</div>
                {STAGES.map(stage => {
                  const count = photos.filter(p => p.stage === stage).length;
                  return (
                    <div key={stage} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: '0.875rem' }}>
                      <span style={{ color: count > 0 ? 'var(--text-primary)' : 'var(--text-muted)', cursor: 'pointer' }}
                        onClick={() => setActiveStage(stage)}>
                        {stage}
                      </span>
                      <span style={{ color: count > 0 ? 'var(--accent-success)' : 'var(--text-muted)', fontWeight: count > 0 ? 600 : 400 }}>
                        {count > 0 ? `${count} photo${count > 1 ? 's' : ''}` : '—'}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Comments */}
            <div className="card">
              <div className="section-title" style={{ marginBottom: 10 }}>Comments ({comments.length})</div>
              <CommentThread comments={comments} rdProjectId={id} onNewComment={handleNewComment} />
            </div>
          </div>
        </div>
      </div>

      {/* Hidden file input */}
      <input ref={fileInputRef} type="file" accept="image/*" capture="environment"
        onChange={handleFileCapture} style={{ display: 'none' }} />

      {/* Photo viewer */}
      {viewerPhoto && (
        <PhotoViewer photo={viewerPhoto} photos={stagePhotos} onClose={() => setViewerPhoto(null)} />
      )}
    </div>
  );
}
